/**
 * Post-checkout return page. Route: /coaching/:slug/enroll/return
 *
 * Square redirects the client here after they complete card entry.
 * We poll `/api/coaching/checkout/status/{session_id}` for up to ~30s
 * looking for the webhook to flip the session to `active`. On success,
 * hard-redirect into the program's Module 1. On timeout, tell the
 * client to check their email for the access link (never revert to
 * "team will be in touch" language).
 */
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Mail } from "lucide-react";
import { SharedHeader, SharedFooter } from "./CoachingProgramPublic";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 30_000;
const PALETTE = { purple: "#4C1D95", gold: "#B8924B", paper: "#FAF8F4", ink: "#0B0B0F" };

export default function CoachingCheckoutReturn() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id") || "";
  const viaEmail = sp.get("via") === "email";
  const [state, setState] = useState(viaEmail ? "timeout" : "polling"); // polling | active | timeout | error
  const [enrollmentId, setEnrollmentId] = useState(null);
  const timerRef = useRef(null);
  useDocumentMeta({
    title: "Confirming your enrollment · KCIS Coaching",
    description: "We're confirming your Square payment and unlocking your program.",
  });

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    if (viaEmail) return; // no polling — email fallback path
    const started = Date.now();
    let cancelled = false;

    async function tick() {
      try {
        const r = await fetch(`${API}/api/coaching/checkout/status/${sessionId}`);
        const data = await r.json();
        if (cancelled) return;
        if (r.ok && data.status === "active") {
          setEnrollmentId(data.enrollment_id || null);
          setState("active");
          // Small delay so the client sees the success moment before we
          // yank them into Module 1.
          if (data.module_1_url) {
            setTimeout(() => { window.location.href = data.module_1_url; }, 1500);
          }
          return;
        }
      } catch {
        /* transient — keep polling */
      }
      if (Date.now() - started > POLL_TIMEOUT_MS) {
        if (!cancelled) setState("timeout");
        return;
      }
      timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    }
    tick();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId, viaEmail]);

  return (
    <div className="min-h-screen"
         style={{ background: PALETTE.paper, color: PALETTE.ink, fontFamily: "'Inter', sans-serif" }}
         data-testid="checkout-return-page">
      <SharedHeader />
      <main className="max-w-xl mx-auto px-6 py-20 text-center">
        {state === "polling" && (
          <div data-testid="checkout-return-polling">
            <div className="w-14 h-14 rounded-full bg-purple-50 mx-auto flex items-center justify-center mb-5">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: PALETTE.purple }} />
            </div>
            <h1 className="font-serif text-3xl text-slate-900">Confirming your payment…</h1>
            <p className="mt-3 text-slate-600 text-[15px] leading-relaxed">
              Square is finalizing the charge. This usually takes just a few seconds.
              Please keep this tab open.
            </p>
          </div>
        )}
        {state === "active" && (
          <div data-testid="checkout-return-active">
            <div className="w-14 h-14 rounded-full bg-emerald-50 mx-auto flex items-center justify-center mb-5">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="font-serif text-3xl text-slate-900">You're in.</h1>
            <p className="mt-3 text-slate-600 text-[15px] leading-relaxed">
              Payment confirmed. Loading Module 1…
            </p>
            {enrollmentId && (
              <p className="mt-4 text-xs text-slate-500">Enrollment: <code>{enrollmentId}</code></p>
            )}
          </div>
        )}
        {state === "timeout" && (
          <div data-testid="checkout-return-timeout">
            <div className="w-14 h-14 rounded-full bg-amber-50 mx-auto flex items-center justify-center mb-5">
              <Mail className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="font-serif text-3xl text-slate-900">Complete payment from your email.</h1>
            <p className="mt-3 text-slate-600 text-[15px] leading-relaxed">
              We've emailed you a secure payment link from Square. Access
              unlocks the moment your payment clears — usually within a
              few minutes of completing card entry.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Link to={`/coaching/${slug}`}>
                <button className="rounded-full bg-white border border-purple-900 text-purple-900 px-6 py-3 text-sm"
                        data-testid="checkout-return-back-btn">
                  ← Back to program overview
                </button>
              </Link>
            </div>
          </div>
        )}
        {state === "error" && (
          <div data-testid="checkout-return-error">
            <h1 className="font-serif text-3xl text-slate-900">Missing session identifier.</h1>
            <p className="mt-3 text-slate-600 text-[15px]">
              Please return to the enrollment page and try again.
            </p>
            <div className="mt-6">
              <Link to={`/coaching/${slug}/enroll`}>
                <button className="rounded-full bg-purple-900 text-white px-6 py-3 text-sm">
                  ← Restart enrollment
                </button>
              </Link>
            </div>
          </div>
        )}
      </main>
      <SharedFooter />
    </div>
  );
}
